import { APIWHATSAPP } from "@/API.js";

/**
 * Categorías y emojis disponibles en el selector
 */
export const CATEGORIAS_EMOJIS = {
  caras: {
    id: "caras",
    label: "Caras",
    icono: "mdi-emoticon-outline",
    emojis: ["😀","😃","😄","😁","😆","😅","😂","🤣","🙂","😉","😊","😇","🥰","😍","🤩","😘","😋","😜","🤑","🤗","🤔","🤐","🤨","😐","😏","😒","🙄","😬","🤥","😌","😔","😪","😴","😷","🤒","🤢","🤧","🤠","🥳","😎","🤓","🧐","😕","😮","😲","😳","🥺","😦","😭","😱","😡","🤬","💀","💩","🤡","👻","🤖"]
  },
  manos: {
    id: "manos",
    label: "Gestos",
    icono: "mdi-hand-okay",
    emojis: ["👍","👎","👏","🙌","🤲","🤝","👊","✊","🤞","✌️","🤟","🤘","👌","🤌","👈","👉","👆","👇","✋","👋","🤙","💪","🙏","👀","👁️"]
  },
  isp: {
    id: "isp",
    label: "Servicios",
    icono: "mdi-router-wireless",
    emojis: ["📶","🌐","💻","🖥️","📱","📞","📡","🔌","🔋","💡","🔧","🔨","⚙️","🛠️","📦","✉️","📧","📝","📄","📅","📊","📈","💰","💵","💳","🧾","🏷️","🔒","🔑","🔔","⚠️","✅","❌","❓","❗","🏠","🏢","🚗","🚚","⏰"]
  },
  simbolos: {
    id: "simbolos",
    label: "Símbolos",
    icono: "mdi-heart-outline",
    emojis: ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","💔","❣️","💕","💓","💗","💖","💘","⭐","🌟","✨","⚡","💥","🔥","💯","🎉","🎁","☕","☀️","🌧️","❄️"]
  }
};

/**
 * Parsea el payload del mensaje memoizando el resultado para evitar JSON.parse repetitivos
 */
export function parsearPayload(msg) {
  if (!msg) return {};
  if (msg._parsedPayload && msg._lastRawPayload === msg.payload) {
    return msg._parsedPayload;
  }
  msg._lastRawPayload = msg.payload;
  if (typeof msg.payload === "object" && msg.payload) {
    msg._parsedPayload = msg.payload;
    return msg.payload;
  }
  if (typeof msg.payload === "string") {
    try {
      const parsed = JSON.parse(msg.payload);
      msg._parsedPayload = parsed;
      return parsed;
    } catch (e) {
      const fallback = { texto: msg.payload };
      msg._parsedPayload = fallback;
      return fallback;
    }
  }
  return {};
}

/**
 * Determina el tipo de contenido del mensaje
 */
export function obtenerTipoMensaje(msg) {
  if (!msg) return "text";
  const p = parsearPayload(msg);
  if (p.plantilla || msg.plantilla || p.meta_payload?.type === "template" || p.type === "template" || msg.tipo === "template" || msg.tipo === "plantilla" || p.tipo === "plantilla") {
    return "template";
  }
  return p.meta_request?.type || p.type || (msg.tipo !== "enviado" && msg.tipo !== "recibido" ? msg.tipo : null) || msg.type || "text";
}

/**
 * Extrae el texto legible o caption del mensaje
 */
export function obtenerTextoMensaje(msg) {
  if (!msg) return "";
  if (typeof msg.texto === "string" && msg.texto.trim()) return msg.texto.trim();
  if (typeof msg.mensaje === "string" && msg.mensaje.trim()) return msg.mensaje.trim();
  if (typeof msg.cuerpo === "string" && msg.cuerpo.trim()) return msg.cuerpo.trim();
  if (typeof msg.body === "string" && msg.body.trim()) return msg.body.trim();
  if (typeof msg.text === "string" && msg.text.trim()) return msg.text.trim();
  if (msg.text?.body) return msg.text.body.trim();

  const p = parsearPayload(msg);
  const req = p.meta_request || msg.meta_request || {};
  const meta = p.meta_payload || msg.meta_payload || {};

  if (req.text) return typeof req.text === "object" ? (req.text.body || "") : req.text;
  if (p.text) return typeof p.text === "object" ? (p.text.body || "") : String(p.text);
  if (p.mensaje) return p.mensaje;
  if (p.cuerpo) return p.cuerpo;
  if (p.texto) return p.texto;
  if (p.caption || req.caption) return p.caption || req.caption;
  if (req.image?.caption || req.document?.caption || req.video?.caption) {
    return req.image?.caption || req.document?.caption || req.video?.caption;
  }
  if (p.image?.caption || p.document?.caption || p.video?.caption) {
    return p.image?.caption || p.document?.caption || p.video?.caption;
  }

  const tpl = meta.template || p.template || req.template;
  if (tpl?.components) {
    return tpl.components.map((c) => c.text || (c.parameters || []).map((param) => param.text || "").join(" ")).filter(Boolean).join(" ");
  }
  return typeof msg.payload === "string" && !msg.payload.trim().startsWith("{") ? msg.payload : "";
}

/**
 * Genera la URL del endpoint proxy para multimedia de Meta WhatsApp
 */
export function obtenerMediaApiUrl(idMedia) {
  if (!idMedia) return null;
  const token = localStorage.getItem("argusblack.token") || "";
  return `${APIWHATSAPP}/api/whatsapp/imagen/${idMedia}?_tk=${encodeURIComponent(token)}`;
}

/**
 * Obtiene la URL de renderizado de imagen, video, audio o sticker
 */
export function obtenerMediaUrl(msg, tipoClave) {
  if (!msg) return null;
  if (msg.url_imagen || msg.media_url) return msg.url_imagen || msg.media_url;
  const p = parsearPayload(msg);
  const req = p.meta_request || msg.meta_request || {};
  const metaMsg = p.entry?.[0]?.changes?.[0]?.value?.messages?.[0] || p.messages?.[0] || {};
  const mediaObj =
    req[tipoClave] ||
    p[tipoClave] ||
    msg[tipoClave] ||
    metaMsg[tipoClave] ||
    (req.type === tipoClave ? req : null) ||
    (p.type === tipoClave ? p : null) ||
    (msg.type === tipoClave ? msg : null) ||
    (metaMsg.type === tipoClave ? metaMsg : null);
  const idMedia =
    mediaObj?.id ||
    (req.type === tipoClave ? req.id : null) ||
    (p.type === tipoClave ? p.id : null) ||
    (metaMsg.type === tipoClave ? metaMsg.id : null);
  if (idMedia) return obtenerMediaApiUrl(idMedia);
  return mediaObj?.link || mediaObj?.url || p.url || msg.url || metaMsg?.url || null;
}

/**
 * Extrae la información de un documento adjunto (url, nombre, icono, color)
 */
export function obtenerDocumentoInfo(msg) {
  const p = parsearPayload(msg);
  const doc = p.meta_request?.document || p.document || {};
  const idMedia = doc.id || (p.type === "document" ? p.id : null);
  const filename = doc.filename || doc.name || p.filename || "Documento adjunto";
  const ext = (filename.includes(".") ? filename.split(".").pop() : "DOC").toUpperCase();
  const url = idMedia ? obtenerMediaApiUrl(idMedia) : (doc.link || doc.url || p.url || null);

  const extLower = ext.toLowerCase();
  let icono = "mdi-file-document-outline";
  let colorClase = "text-primary";
  if (extLower === "pdf") { icono = "mdi-file-pdf-box"; colorClase = "text-danger"; }
  else if (["doc", "docx"].includes(extLower)) { icono = "mdi-file-word-box"; colorClase = "text-primary"; }
  else if (["xls", "xlsx", "csv"].includes(extLower)) { icono = "mdi-file-excel-box"; colorClase = "text-success"; }
  else if (["zip", "rar", "7z", "tar", "gz"].includes(extLower)) { icono = "mdi-folder-zip"; colorClase = "text-warning"; }
  else if (["jpg", "jpeg", "png", "webp", "gif"].includes(extLower)) { icono = "mdi-file-image"; colorClase = "text-purple"; }

  return { url, filename, extension: ext, icono, colorClase };
}

/**
 * Obtiene el nombre del archivo si existe
 */
export function obtenerNombreArchivo(msg) {
  if (!msg) return "";
  const doc = obtenerDocumentoInfo(msg);
  return doc?.filename && doc.filename !== "Documento adjunto" ? doc.filename : "";
}

/**
 * Extrae la información de una plantilla oficial de WhatsApp
 */
export function obtenerPlantillaInfo(msg) {
  if (!msg) return {};
  const p = parsearPayload(msg);
  const meta = p.meta_payload || msg.meta_payload || {};
  const tpl = meta.template || p.template || msg.template || {};

  const tipoEncabezado = (msg.tipo_encabezado || p.tipo_encabezado || "").toUpperCase();
  const encabezado = msg.encabezado || p.encabezado || "";
  let cuerpo = msg.cuerpo || p.cuerpo || msg.mensaje || p.mensaje || "";
  const pie = msg.pie || p.pie || "";

  if (!cuerpo && tpl.components) {
    const bodyComp = tpl.components.find((c) => c.type === "body");
    if (bodyComp) cuerpo = (bodyComp.parameters || []).map((param) => param.text || "").filter(Boolean).join(" ");
  }
  if (!cuerpo) cuerpo = obtenerTextoMensaje(msg);

  let imagenUrl = null;
  if ((tipoEncabezado === "IMAGE" || typeof encabezado === "string") && (encabezado.startsWith("http://") || encabezado.startsWith("https://"))) {
    imagenUrl = encabezado;
  } else if (tpl.components) {
    const headerComp = tpl.components.find((c) => c.type === "header");
    const imgParam = headerComp?.parameters?.find((param) => param.type === "image" || param.image);
    const idMedia = imgParam?.image?.id || imgParam?.id;
    if (idMedia) imagenUrl = obtenerMediaApiUrl(idMedia);
    else if (imgParam?.image?.link) imagenUrl = imgParam.image.link;
  }

  return { tipoEncabezado, encabezado, cuerpo, pie, imagenUrl };
}

/**
 * Extrae datos de ubicación geográfica
 */
export function obtenerUbicacionInfo(msg) {
  const p = parsearPayload(msg);
  const loc = p.meta_request?.location || p.location || p.ubicacion || (msg.latitude ? msg : null);
  if (!loc) return null;
  const lat = parseFloat(loc.latitude || loc.latitud || 0);
  const lng = parseFloat(loc.longitude || loc.longitud || 0);
  return {
    name: loc.name || loc.nombre || "",
    address: loc.address || loc.direccion || "",
    url: loc.url || `https://www.google.com/maps?q=${lat},${lng}`,
    coordenadasFormateadas: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
  };
}

/**
 * Identifica si un video es un GIF animado
 */
export function esGif(msg, mapaVideosGif = {}) {
  const key = msg.id || msg.wamid;
  if (key && mapaVideosGif[key] !== undefined) return mapaVideosGif[key];
  const p = parsearPayload(msg);
  return p.type === "gif" || p.meta_request?.type === "gif" || msg.tipo === "gif" || Boolean(p.video?.animated || p.video?.gif);
}

/**
 * Cuenta si un mensaje consiste únicamente de 1, 2 o 3 emojis grandes
 */
export function obtenerConteoEmojis(msg) {
  if (obtenerTipoMensaje(msg) !== "text") return 0;
  const t = (obtenerTextoMensaje(msg) || "").trim();
  if (!t) return 0;
  const emojiRegex = /(\p{Extended_Pictographic}|\p{Emoji_Presentation}|[0-9#*]\uFE0F?\u20E3|[\u{1F1E6}-\u{1F1FF}]{2})(?:[\u{1F3FB}-\u{1F3FF}]|\uFE0F|\u200D(?:\p{Extended_Pictographic}|\p{Emoji_Presentation}))*/gu;
  const matches = t.match(emojiRegex);
  return matches && t.replace(emojiRegex, "").replace(/\s+/g, "").length === 0 ? matches.length : 0;
}

export function esMensajeMedia(msg) {
  const tipo = obtenerTipoMensaje(msg);
  return tipo === "image" || tipo === "video" || tipo === "gif";
}

export function esMensajeSoloMedia(msg) {
  return esMensajeMedia(msg) && !obtenerTextoMensaje(msg);
}

/**
 * Parseo y formato de fechas y horas
 */
export function obtenerObjetoFecha(msg) {
  if (!msg) return new Date();
  if (msg instanceof Date) return msg;

  const p = parsearPayload(msg);
  const fechaStr =
    msg.created_at ||
    msg.fecha_envio ||
    msg.fecha_entrega ||
    msg.fecha_lectura ||
    msg.updated_at ||
    msg.fecha ||
    msg.timestamp ||
    p.timestamp ||
    p.created_at ||
    p.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.timestamp;

  if (fechaStr) {
    if (fechaStr instanceof Date) return fechaStr;
    if (!isNaN(fechaStr) && String(fechaStr).length >= 10 && !String(fechaStr).includes("-")) {
      const ts = Number(fechaStr) * (String(fechaStr).length === 10 ? 1000 : 1);
      const d = new Date(ts);
      if (!isNaN(d.getTime())) return d;
    }
    const d = new Date(String(fechaStr).replace(" ", "T"));
    if (!isNaN(d.getTime())) return d;
  }
  return new Date();
}

export function obtenerHoraMensaje(msg) {
  if (msg.hora) return msg.hora;
  return obtenerObjetoFecha(msg).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function mostrarSeparadorFecha(msg, index, lista) {
  if (index === 0) return true;
  const d1 = obtenerObjetoFecha(msg).toDateString();
  const d2 = obtenerObjetoFecha(lista[index - 1]).toDateString();
  return d1 !== d2;
}

export function obtenerEtiquetaFecha(msg) {
  const d = obtenerObjetoFecha(msg);
  const hoy = new Date();
  if (d.toDateString() === hoy.toDateString()) return "HOY";
  const ayer = new Date();
  ayer.setDate(ayer.getDate() - 1);
  if (d.toDateString() === ayer.toDateString()) return "AYER";
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }).toUpperCase();
}

/**
 * Identificadores únicos y Wamid
 */
export function obtenerWamid(msg) {
  if (!msg) return "";
  if (msg.wamid) return String(msg.wamid);
  if (typeof msg.id === "string" && msg.id.startsWith("wamid.")) return msg.id;
  const p = parsearPayload(msg);
  if (p.wamid) return String(p.wamid);
  if (p.id && typeof p.id === "string" && p.id.startsWith("wamid.")) return p.id;
  if (p.messages?.[0]?.id) return p.messages[0].id;
  if (p.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.id) return p.entry[0].changes[0].value.messages[0].id;
  if (p.meta_response?.messages?.[0]?.id) return p.meta_response.messages[0].id;
  return "";
}

export function coincideId(msg, targetId) {
  if (!msg || !targetId) return false;
  const target = String(targetId).trim();
  if (String(msg.id) === target) return true;
  const wamid = obtenerWamid(msg);
  return Boolean(wamid && wamid === target);
}

export function obtenerKeyMensajeId(msg) {
  return obtenerWamid(msg) || msg.id;
}

export function obtenerReaccion(msg) {
  if (!msg) return null;
  if (msg.reaccion) return String(msg.reaccion).trim() || null;
  if (typeof msg.reaction === "string") return msg.reaction.trim() || null;
  if (msg.reaction?.emoji) return msg.reaction.emoji.trim() || null;
  const p = parsearPayload(msg);
  return (p.reaccion || p.reaction?.emoji || p.reaction || "").trim() || null;
}

/**
 * Dirección y estado del mensaje
 */
export function esSaliente(msg, chat = null) {
  if (!msg) return false;
  if (msg.direccion === "saliente") return true;
  if (msg.direccion === "entrante") return false;
  if (msg.tipo === "enviado") return true;
  if (msg.tipo === "recibido") return false;
  if (typeof msg.id === "string" && msg.id.startsWith("temp_")) return true;

  const p = parsearPayload(msg);
  if (p.direccion === "saliente") return true;
  if (p.direccion === "entrante") return false;
  if (p.tipo === "enviado") return true;
  if (p.tipo === "recibido") return false;

  // Si tiene destinatario o 'to', y coincide con el número del contacto, es saliente
  const numContacto = (chat?.numero || msg.telefono_cliente?.numero || "").replace(/\D/g, "");
  const numDestinatario = String(msg.destinatario || p.to || p.meta_request?.to || "").replace(/\D/g, "");
  const numRemitente = String(msg.from || msg.remitente || p.from || p.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.from || "").replace(/\D/g, "");

  if (numContacto) {
    const diezContacto = numContacto.slice(-10);
    if (numDestinatario && (numDestinatario.includes(diezContacto) || diezContacto.includes(numDestinatario))) {
      return true;
    }
    if (numRemitente && (numRemitente.includes(diezContacto) || diezContacto.includes(numRemitente))) {
      return false;
    }
  }

  // Si tiene context de respuesta enviado por el sistema
  if (msg.context?.es_saliente || p.context?.es_saliente) return true;

  return false;
}

export function esErrorVentana24Horas(msg) {
  if (!msg) return false;
  const esFallo = msg.estado === "failed" || msg.error_mensaje || msg.error_detalles || msg.error_detalle || msg.palomitas === "fallido";
  if (!esFallo) return false;

  let strError = "";
  if (typeof msg.error_detalles === "string") strError += " " + msg.error_detalles;
  else if (msg.error_detalles && typeof msg.error_detalles === "object") strError += " " + JSON.stringify(msg.error_detalles);
  if (typeof msg.error_detalle === "string") strError += " " + msg.error_detalle;
  else if (msg.error_detalle && typeof msg.error_detalle === "object") strError += " " + JSON.stringify(msg.error_detalle);
  if (msg.error_mensaje) strError += " " + msg.error_mensaje;
  if (msg.codigo_error) strError += " " + msg.codigo_error;
  if (msg.error_code) strError += " " + msg.error_code;

  const p = parsearPayload(msg);
  if (p.error) strError += " " + JSON.stringify(p.error);
  if (p.error_data) strError += " " + JSON.stringify(p.error_data);
  if (p.error_detalles) strError += " " + JSON.stringify(p.error_detalles);
  if (p.error_detalle) strError += " " + JSON.stringify(p.error_detalle);

  const errLower = strError.toLowerCase();
  return (
    errLower.includes("131047") ||
    errLower.includes("24 hours") ||
    errLower.includes("24 horas") ||
    errLower.includes("re-engagement") ||
    errLower.includes("more than 24 hours have passed") ||
    errLower.includes("last replied to this number")
  );
}

export function obtenerMensajeErrorLegible(msg) {
  if (!msg) return "Error en el envío";
  if (esErrorVentana24Horas(msg)) {
    return "No se pudo enviar este mensaje porque la ventana de 24 horas ha expirado. Inicia la conversación con una plantilla oficial.";
  }
  if (typeof msg.error_mensaje === "string" && msg.error_mensaje.trim()) {
    return msg.error_mensaje.trim();
  }
  const errDet = msg.error_detalles || msg.error_detalle;
  if (errDet) {
    if (typeof errDet === "string") {
      try {
        const obj = JSON.parse(errDet);
        return obj.details || obj.message || errDet;
      } catch (e) {
        return errDet;
      }
    } else if (typeof errDet === "object") {
      return errDet.details || errDet.message || JSON.stringify(errDet);
    }
  }
  return "No se pudo enviar el mensaje";
}

export function obtenerClasePalomitas(msg) {
  if (!msg) return "";
  if (esErrorVentana24Horas(msg) || msg.fecha_fallo || msg.estado === "failed" || msg.estado === "fallido" || msg.palomitas === "fallido") {
    return "mdi mdi-alert-circle text-danger";
  }
  const st = msg.palomitas || msg.estado;
  // 1. Leído -> Doble palomita azul
  if (msg.fecha_leido || msg.fecha_lectura || st === "leido" || st === "read") {
    return "mdi mdi-check-all text-primary";
  }
  // 2. Entregado -> Doble palomita gris
  if (msg.fecha_entrega || st === "entregado" || st === "delivered") {
    return "mdi mdi-check-all text-muted";
  }
  // 3. Enviado -> Una sola palomita gris
  if (msg.fecha_envio || st === "enviado" || st === "sent") {
    return "mdi mdi-check text-muted";
  }
  // 4. Pendiente / Reloj
  return "mdi mdi-clock-outline text-muted";
}

/**
 * Funciones de contacto
 */
export function obtenerIdCliente(chat) {
  if (!chat) return null;
  return chat.id_cliente || (chat.cliente && chat.cliente.id) || null;
}

export function esClienteGenerico(chat) {
  if (!chat) return true;
  const nom = ((chat.cliente && chat.cliente.nombre) || chat.nombre || "").toLowerCase().trim();
  return (chat.cliente && chat.cliente.generico) || nom === "varios" || nom.includes("publico en general");
}

export function nombreContacto(chat) {
  if (!chat) return "";
  if (esClienteGenerico(chat)) {
    return (chat.prefijo ? chat.prefijo + " " : "") + (chat.numero || "Sin número");
  }
  if (chat.cliente?.nombre) {
    return `${chat.cliente.nombre} ${chat.cliente.apellido_paterno || ""} ${chat.cliente.apellido_materno || ""}`.trim();
  }
  return chat.nombre || chat.numero || "Sin nombre";
}

export function inicialContacto(chat) {
  return ((chat?.cliente?.nombre || chat?.nombre || "C").charAt(0)).toUpperCase();
}

/**
 * Contexto y mensajes citados
 */
export function extraerContextId(msg) {
  if (!msg) return null;
  if (msg.context?.id) return String(msg.context.id).trim();
  if (msg.context?.message_id) return String(msg.context.message_id).trim();
  const p = parsearPayload(msg);
  return p.context?.id || p.context?.message_id || p.meta_request?.context?.message_id || null;
}

export function obtenerMensajeCitado(msg, listaMensajes = []) {
  if (!msg) return null;
  const targetId = extraerContextId(msg);
  if (!targetId) return null;
  const encontrado = (listaMensajes || []).find((m) => m && m !== msg && coincideId(m, targetId));
  if (encontrado) return encontrado;

  const p = parsearPayload(msg);
  const ctx = msg.context || p.context || p.meta_request?.context || {};
  const esSalienteCtx = ctx.es_saliente_citado || ctx.es_saliente || ctx.direccion === "saliente" || ctx.from === "me";
  return {
    id: targetId,
    wamid: targetId,
    from: ctx.from || (esSalienteCtx ? "me" : ""),
    direccion: esSalienteCtx ? "saliente" : (ctx.direccion || ""),
    texto: ctx.texto || ctx.text || ctx.caption || "Mensaje citado",
    tipo: ctx.tipo || ctx.type || "text",
    esPlaceholder: true
  };
}

/**
 * Gestión de caché LocalStorage
 */
export function obtenerKeyCache(chat) {
  if (!chat) return null;
  const id = chat.id || chat.id_telefono_cliente || chat.id_telefono || chat.id_cliente;
  const num = (chat.numero || "").replace(/\D/g, "");
  return `argusblack.whatsapp.chat_${id || num}`;
}

export function cargarConversacionDeCache(chat) {
  if (!chat) return null;
  const id = chat.id || chat.id_telefono_cliente || chat.id_telefono || chat.id_cliente;
  const num = (chat.numero || "").replace(/\D/g, "");
  const keys = [
    id ? `argusblack.whatsapp.chat_${id}` : null,
    num ? `argusblack.whatsapp.chat_${num}` : null
  ].filter(Boolean);

  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.mensajes) && parsed.mensajes.length > 0) return parsed;
        if (Array.isArray(parsed) && parsed.length > 0) return { mensajes: parsed };
      }
    } catch (e) {}
  }
  return null;
}

export function guardarConversacionEnCache(chat, mensajes) {
  if (!chat) return null;
  const id = chat.id || chat.id_telefono_cliente || chat.id_telefono;

  try {
    const rawList = Array.isArray(mensajes) ? mensajes : [];
    // NUNCA persistir mensajes temporales en caché local para evitar duplicaciones tras recargar (F5)
    const msgs = rawList.filter((m) => !(m && typeof m.id === "string" && m.id.startsWith("temp_"))).slice(-150);
    const ahora = new Date().toISOString();

    const infoConversacion = {
      id,
      id_cliente: obtenerIdCliente(chat),
      numero: chat.numero || "",
      prefijo: chat.prefijo || "",
      nombre: nombreContacto(chat),
      contacto: {
        id,
        id_cliente: obtenerIdCliente(chat),
        nombre: chat.nombre || "",
        numero: chat.numero || "",
        prefijo: chat.prefijo || "",
        principal: Boolean(chat.principal),
        avatarCliente: chat.avatarCliente || null,
        avatarWhatsapp: chat.avatarWhatsapp || null,
        cliente: chat.cliente ? {
          id: chat.cliente.id,
          nombre: chat.cliente.nombre,
          apellido_paterno: chat.cliente.apellido_paterno,
          apellido_materno: chat.cliente.apellido_materno,
          generico: chat.cliente.generico
        } : null
      },
      mensajes: msgs,
      total_mensajes: msgs.length,
      ultima_actualizacion: ahora
    };

    const jsonStr = JSON.stringify(infoConversacion);
    if (id) localStorage.setItem(`argusblack.whatsapp.chat_${id}`, jsonStr);
    try {
      localStorage.removeItem("argusblack.whatsapp.chat_activo");
    } catch (e) {}
    return ahora;
  } catch (e) {
    console.warn("[WhatsApp Cache] Error al guardar en LocalStorage:", e);
    purgarCachesAntiguas();
    return null;
  }
}

export function purgarCachesAntiguas() {
  try {
    const prefijo = "argusblack.whatsapp.chat_";
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefijo)) keys.push(k);
    }
    if (keys.length > 50) {
      keys.slice(0, 15).forEach((k) => localStorage.removeItem(k));
    }
  } catch (e) {}
}

export function eliminarCacheChat(chat) {
  if (!chat) return;
  const id = chat.id || chat.id_telefono_cliente || chat.id_telefono;
  const num = (chat.numero || "").replace(/\D/g, "");
  try {
    if (id) localStorage.removeItem(`argusblack.whatsapp.chat_${id}`);
    if (num) {
      const rawNum = localStorage.getItem(`argusblack.whatsapp.chat_${num}`);
      if (rawNum) {
        try {
          const parsed = JSON.parse(rawNum);
          if (!parsed.id || String(parsed.id) === String(id)) {
            localStorage.removeItem(`argusblack.whatsapp.chat_${num}`);
          }
        } catch (e) {
          localStorage.removeItem(`argusblack.whatsapp.chat_${num}`);
        }
      }
    }
    const activoRaw = localStorage.getItem("argusblack.whatsapp.chat_activo");
    if (activoRaw) {
      const activo = JSON.parse(activoRaw);
      const targetId = chat.id || chat.id_telefono_cliente || chat.id_telefono;
      if (activo && (String(activo.id) === String(targetId))) {
        localStorage.removeItem("argusblack.whatsapp.chat_activo");
      }
    }
  } catch (e) {}
}

